/**
 * Permission utilities for profile-based access control.
 * 
 * This module provides utility functions and hooks for checking user permissions
 * and restrictions based on their profile.
 */

import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import type { UserProfile } from '../types/auth';

/**
 * Hook to get current user permissions and restrictions
 */
export const usePermissions = () => {
  const { user, profile, accessibleRoutes, restrictions } = useSelector((state: RootState) => state.auth);
  
  return {
    user,
    profile,
    accessibleRoutes,
    restrictions,
    
    // Helper functions
    hasPermission: (permission: string): boolean => {
      return profile?.permissions.includes(permission) ?? false;
    },
    
    canAccessRoute: (route: string): boolean => {
      return accessibleRoutes.some(accessibleRoute => 
        route === accessibleRoute || route.startsWith(accessibleRoute)
      );
    },
    
    isFeatureRestricted: (feature: string): boolean => {
      return restrictions.buttons.includes(feature) || 
             restrictions.actions.includes(feature);
    },
    
    isNavigationRestricted: (navItem: string): boolean => {
      return restrictions.navigation.includes(navItem);
    },
    
    isPageRestricted: (page: string): boolean => {
      return restrictions.pages.includes(page);
    }
  };
};

/**
 * Profile-based permission constants
 */
export const Permissions = {
  // User Management
  MANAGE_USERS: 'manage_users',
  CREATE_USERS: 'create_users',
  DELETE_USERS: 'delete_users',
  VIEW_USERS: 'view_users',
  
  // Institution Management
  MANAGE_INSTITUTIONS: 'manage_institutions',
  VIEW_INSTITUTIONS: 'view_institutions',
  
  // System Configuration
  SYSTEM_CONFIG: 'system_config',
  VIEW_SYSTEM_CONFIG: 'view_system_config',
  MANAGE_AI_CONFIG: 'manage_ai_config',
  
  // Report Management
  CREATE_REPORTS: 'create_reports',
  EDIT_REPORTS: 'edit_reports',
  DELETE_REPORTS: 'delete_reports',
  VIEW_REPORTS: 'view_reports',
  
  // Patient Management
  CREATE_PATIENTS: 'create_patients',
  EDIT_PATIENTS: 'edit_patients',
  DELETE_PATIENTS: 'delete_patients',
  VIEW_PATIENTS: 'view_patients',
  VIEW_ALL_PATIENTS: 'view_all_patients',
  
  // DICOM Management
  MANAGE_DICOM: 'manage_dicom',
  VIEW_DICOM: 'view_dicom',
  
  // Worklist Management
  VIEW_WORKLIST: 'view_worklist',
  MANAGE_WORKLIST: 'manage_worklist',
  CREATE_WORKLIST: 'create_worklist',
  EDIT_WORKLIST: 'edit_worklist',
  DELETE_WORKLIST: 'delete_worklist',
  
  // General Access
  LOGIN: 'login',
  VIEW_DASHBOARD: 'view_dashboard'
} as const;

/**
 * Profile information with labels for UI display
 */
export const ProfileLabels: Record<UserProfile, string> = {
  'superadmin': 'Super Administrador',
  'institution-admin': 'Administrador de Institución',
  'medico': 'Médico',
  'asistente': 'Asistente',
  'radiologo': 'Radiólogo',
  'tecnico': 'Técnico',
  'enfermera': 'Enfermera'
};

/**
 * Get user-friendly profile label
 */
export const getProfileLabel = (profile: UserProfile): string => {
  return ProfileLabels[profile] || profile;
};

/**
 * Check if user profile has specific permission
 */
export const hasPermission = (userPermissions: string[], permission: string): boolean => {
  return userPermissions.includes(permission);
};

/**
 * Check if user can access specific route
 */
export const canAccessRoute = (accessibleRoutes: string[], route: string): boolean => {
  return accessibleRoutes.some(accessibleRoute => 
    route === accessibleRoute || route.startsWith(accessibleRoute)
  );
};

/**
 * Get restricted menu items for a user
 */
export const getRestrictedMenuItems = (restrictions: {
  navigation: string[];
  buttons: string[];
  pages: string[];
  actions: string[];
}): string[] => {
  return restrictions.navigation;
};

/**
 * Permission-based component wrapper
 */
export interface PermissionGuardProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  fallback?: React.ReactNode;
  requireAll?: boolean; // If true, user must have ALL permissions. If false, user needs ANY permission
}

/**
 * Component to conditionally render content based on permissions
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({ 
  children, 
  permission, 
  permissions = [], 
  fallback = null,
  requireAll = false 
}) => {
  const { hasPermission: userHasPermission, profile } = usePermissions();
  
  // If no permissions specified, render children
  if (!permission && permissions.length === 0) {
    return <>{children}</>;
  }
  
  // Check single permission
  if (permission) {
    return userHasPermission(permission) ? <>{children}</> : <>{fallback}</>;
  }
  
  // Check multiple permissions
  const permissionChecks = permissions.map(p => userHasPermission(p));
  const hasRequiredPermissions = requireAll 
    ? permissionChecks.every(check => check)
    : permissionChecks.some(check => check);
  
  return hasRequiredPermissions ? <>{children}</> : <>{fallback}</>;
};

/**
 * Profile-based component wrapper
 */
export interface ProfileGuardProps {
  children: React.ReactNode;
  profiles: UserProfile[];
  fallback?: React.ReactNode;
}

/**
 * Component to conditionally render content based on user profile
 */
export const ProfileGuard: React.FC<ProfileGuardProps> = ({ 
  children, 
  profiles, 
  fallback = null 
}) => {
  const { user } = usePermissions();
  
  if (!user || !profiles.includes(user.profile)) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
};

/**
 * Route access utilities
 */
export const RouteAccess = {
  // Admin routes
  ADMIN_USERS: '/admin/users',
  ADMIN_INSTITUTIONS: '/admin/institutions',
  ADMIN_CONFIG: '/admin/config',
  
  // Dashboard routes
  DASHBOARD: '/dashboard',
  DASHBOARD_PATIENTS: '/dashboard/patients',
  DASHBOARD_REPORTS: '/dashboard/reports',
  DASHBOARD_USERS: '/dashboard/users',
  DASHBOARD_INSTITUTIONS: '/dashboard/institutions',
  DASHBOARD_CONFIG: '/dashboard/config',
  
  // Feature routes
  REPORTS_CREATE: '/reports/create',
  REPORTS_LIST: '/reports',
  PATIENTS_LIST: '/patients',
  DICOM_VIEWER: '/dicom',
  
  // Settings
  SETTINGS: '/settings'
} as const;

/**
 * Navigation items that can be restricted
 */
export const NavigationItems = {
  DASHBOARD: 'Dashboard',
  PATIENTS: 'Pacientes', 
  REPORTS: 'Reportes',
  USER_MANAGEMENT: 'Gestión de Usuarios',
  INSTITUTION_MANAGEMENT: 'Gestión de Instituciones',
  SYSTEM_CONFIG: 'Configuración del Sistema',
  DICOM: 'DICOM',
  SETTINGS: 'Configuración'
} as const;

/**
 * Features that can be restricted
 */
export const Features = {
  CREATE_REPORT: 'create_report',
  DELETE_REPORT: 'delete_report',
  CREATE_USER: 'create_user',
  DELETE_USER: 'delete_user',
  EXPORT_DATA: 'export_data',
  SYSTEM_SETTINGS: 'system_settings'
} as const;
